import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session key to track if header animation has played
const HEADER_ANIMATION_KEY = "birk_header_animated";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0 = top, 1 = past hero
  const [brandHovered, setBrandHovered] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [leafLoaded, setLeafLoaded] = useState(false);
  const [siteConfig, setSiteConfig] = useState({ phone: "+45 12 34 56 78", email: "", cvr: "" });
  const location = useLocation();
  const navigate = useNavigate();
  
  // Header height constant
  const HEADER_HEIGHT = 80;
  
  // Check for reduced motion preference
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && 
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Preload leaf image and trigger header fade-in when ready
  useEffect(() => {
    // Skip animation if reduced motion is preferred
    if (prefersReducedMotion.current) {
      setHeaderVisible(true);
      setLeafLoaded(true);
      return;
    }
    
    // Check if animation has already played this session
    const hasAnimated = sessionStorage.getItem(HEADER_ANIMATION_KEY);
    
    if (hasAnimated) {
      // Already animated this session, show immediately
      setHeaderVisible(true);
      setLeafLoaded(true);
    } else {
      // First load - preload the leaf image first
      const img = new Image();
      img.onload = () => {
        setLeafLoaded(true);
        // Small delay after image loads to ensure smooth start
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeaderVisible(true);
            sessionStorage.setItem(HEADER_ANIMATION_KEY, "true");
          });
        });
      };
      img.onerror = () => {
        // Even on error, show the header
        setLeafLoaded(true);
        setHeaderVisible(true);
        sessionStorage.setItem(HEADER_ANIMATION_KEY, "true");
      };
      img.src = "/brand/leaf.png";
    }
  }, []);

  // Scroll progress calculation based on hero height
  useEffect(() => {
    const calculateScrollProgress = () => {
      // Find hero element (first section on homepage)
      const hero = document.querySelector('[data-testid="hero-section"]');
      
      if (!hero) {
        // No hero found, use fallback behavior
        setScrollProgress(window.scrollY > 50 ? 1 : 0);
        return;
      }
      
      // Get hero bottom position
      const heroRect = hero.getBoundingClientRect();
      const heroBottom = heroRect.bottom;
      
      // Calculate progress: 0 when hero bottom is at/below header, 1 when hero is scrolled past
      // heroBottom starts at ~heroHeight, ends at HEADER_HEIGHT when fully scrolled
      const scrollDistance = heroRect.height - HEADER_HEIGHT;
      const currentScroll = window.scrollY;
      
      // Clamp progress between 0 and 1
      const progress = Math.min(1, Math.max(0, currentScroll / scrollDistance));
      setScrollProgress(progress);
    };

    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        calculateScrollProgress();
        rafId = null;
      });
    };

    // Initial calculation
    calculateScrollProgress();
    
    // Listen for scroll
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Recalculate on resize/orientation change
    window.addEventListener("resize", calculateScrollProgress);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", calculateScrollProgress);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [location.pathname]); // Recalculate when route changes

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/config`);
        setSiteConfig(res.data);
      } catch (err) {
        console.error("Error fetching config:", err);
      }
    };
    fetchConfig();
  }, []);

  // Format phone for tel: link (remove spaces)
  const phoneLink = `tel:${siteConfig.phone.replace(/\s+/g, '')}`;

  const navLinks = [
    { name: "Forside", path: "/" },
    { name: "Ydelser", path: "/ydelser" },
    { name: "Galleri", path: "/galleri" },
    { name: "Om os", path: "/om-os" },
    { name: "Kontakt", path: "/kontakt" },
  ];

  const isHome = location.pathname === "/";
  // On hero = over hero image (scroll progress < 1 AND on homepage)
  const isOnHero = scrollProgress < 1 && isHome;

  // Detect if device supports hover (desktop) vs touch-only (mobile)
  const [isDesktop, setIsDesktop] = useState(true);
  
  useEffect(() => {
    // Check if device has hover capability (desktop/laptop)
    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    setIsDesktop(mediaQuery.matches);
    
    const handler = (e) => setIsDesktop(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Brand text color - only change on desktop hover
  const getBrandTextColor = () => {
    if (isDesktop && brandHovered) {
      return "text-[#0F2620]"; // Darker green on hover (desktop only)
    }
    return "text-[#1A3C34]"; // Default green
  };

  // Leaf should always show original green color (no invert needed with white bg)
  const shouldInvertLeaf = false;
  
  // Only apply hover transforms on desktop
  const getBrandTransform = () => {
    if (!isDesktop) return "none"; // No transform on mobile ever
    if (brandHovered) return "scale(1.05)";
    return "none";
  };

  // Handle logo click - always go to top of homepage
  const handleLogoClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === "/") {
      // Already on homepage - smooth scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Navigate to homepage (will show hero at top)
      navigate("/");
    }
  };

  // Calculate header background opacity (40% to 100% based on scroll progress)
  const headerBgOpacity = isHome ? 0.4 + (scrollProgress * 0.6) : 1;
  const showShadow = scrollProgress > 0.8 || !isHome;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-200 ease-out ${
        showShadow ? "shadow-md" : ""
      }`}
      style={{
        backgroundColor: `rgba(255, 255, 255, ${headerBgOpacity})`,
        backdropFilter: headerBgOpacity < 1 ? "blur(8px)" : "none",
        WebkitBackdropFilter: headerBgOpacity < 1 ? "blur(8px)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header content wrapper - fades in together on mobile */}
        <div 
          className="flex justify-between items-center h-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transition: prefersReducedMotion.current 
              ? "none" 
              : "opacity 2000ms ease-out",
          }}
        >
          {/* Brand: Text + Leaf as ONE link */}
          <a
            href="/"
            onClick={handleLogoClick}
            data-testid="logo-link"
            onMouseEnter={() => isDesktop && setBrandHovered(true)}
            onMouseLeave={() => isDesktop && setBrandHovered(false)}
            className={`logo-link inline-flex items-center gap-2 md:gap-2.5 font-bold text-xl md:text-2xl tracking-tight origin-left select-none cursor-pointer ${
              getBrandTextColor()
            }`}
            style={{ 
              fontFamily: "Manrope, sans-serif",
              WebkitTapHighlightColor: "transparent",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
              outline: "none",
              boxShadow: "none",
              transform: getBrandTransform(),
              transition: prefersReducedMotion.current 
                ? "none" 
                : "transform 200ms ease-out, color 200ms ease-out",
            }}
          >
            <span>Birk Anlæg</span>
            {/* Only render img after leaf is preloaded to prevent flash */}
            {leafLoaded && (
              <img
                src="/brand/leaf.png"
                alt=""
                className="h-8 md:h-11 w-auto pointer-events-none"
                draggable={false}
                style={{
                  filter: shouldInvertLeaf ? "brightness(0) invert(1)" : "none",
                }}
              />
            )}
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              // Always green text since navbar has white/translucent background
              const linkClasses = "text-[#1A3C34] hover:text-[#0F2620] transition-all duration-200 ease-out hover:scale-105 origin-center inline-block";
              
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                  className={linkClasses}
                >
                  {link.name}
                </Link>
              );
            })}
            <a
              href={phoneLink}
              data-testid="phone-cta"
              className="flex items-center gap-2 bg-[#1A3C34] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#142F29] transition-all duration-200 ease-out hover:scale-105 hover:shadow-lg origin-center"
            >
              <Phone size={18} />
              <span className="hidden lg:inline">Ring til os</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-[#1A3C34]"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden bg-white border-t border-gray-100 mobile-menu-enter"
          >
            <div className="py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                  className="block px-4 py-3 text-[#1A3C34] hover:bg-gray-50 transition-colors"
                >
                  {link.name}
                </Link>
              ))}
              <a
                href={phoneLink}
                data-testid="mobile-phone-cta"
                className="flex items-center gap-2 mx-4 mt-4 bg-[#1A3C34] text-white px-5 py-3 rounded-full font-medium justify-center"
              >
                <Phone size={18} />
                Ring til os
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
